import { formatISO } from "date-fns";
import format from "date-fns/format";
import { action, flow, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { Button } from "primereact/button";
import React, { useContext } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { AddExperienceToCandidateResponse } from "../../../../api/candidate/addExperienceToCandidate";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { redirect } from "../../../../WSUtils/urlUtils";
import { invalidateQueries } from "../../../../WSUtils/useLatestQuery";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames, defDFnsOpt } from "../../../../WSUtils/util";
import {
  CVPSearchParams,
  queryStringStore,
} from "../../../../WSUtils/wsQueryStringStore";
import { CVEditContext } from "../../CVEditContext";
import { CVEditListStyles } from "../../CVEditListStyles";
import { CVEditExperience } from "./CVEditExperience";
import { onCveExperienceEditorCloseButtonClick } from "./CVEditExperienceEditor";
import { CVEditExperienceSegmentStore } from "./CVEditExperienceSegmentStore";

export interface CVEditExperienceListProps {
  experienceSegmentStore: CVEditExperienceSegmentStore;
}

export const CVEditExperienceList = observer(
  ({ experienceSegmentStore }: CVEditExperienceListProps) => {
    const L = useLocStore(LocContext.CV);
    const cvEditContext = useContext(CVEditContext);
    const userInfo = useContext(UserInfoContext);
    const queryClient = useQueryClient();

    const deletedItems = experienceSegmentStore.deletedListItems;
    const items = experienceSegmentStore.listItems;

    if (experienceSegmentStore.nTotalListItems === 0) return null;

    const deletedItemsComponent = (
      <Observer
        render={() => (
          <>
            {deletedItems.map((cveExp: CVEditExperience) => (
              <div
                key={cveExp.id}
                className={concatClassnames([
                  "cve-listing",
                  "cve-listing-deleted",
                ])}
              >
                <div className="p-inline-message p-component p-inline-message-info">
                  <div aria-live="polite" role="alert">
                    <span className="p-inline-message-icon pi pi-info-circle"></span>
                    <span className="p-inline-message-text">
                      {L.id("a06839cbf8ef").loc("Slettet")}
                    </span>
                  </div>
                  <Button
                    className="p-button-text"
                    label={L.id("d892a83eba7e").loc("Angre")}
                    onClick={(evt) => {
                      evt.preventDefault();

                      const candidateAPI = userInfo.fullAPI.candidateAPI;

                      const restore = flow(function* () {
                        const result: AddExperienceToCandidateResponse =
                          yield candidateAPI.addExperienceToCandidate({
                            showSentryDialogOnError: true,
                            userInfo,

                            //
                            comment: cveExp.description,
                            from: cveExp.from ? formatISO(cveExp.from) : null,
                            to: cveExp.to ? formatISO(cveExp.to) : null,
                            firm: cveExp.employer,
                            position: cveExp.position,
                            industryType: cveExp.industryType,

                            //
                            hide: false,
                            customerId: 0,
                            level: 0,
                            references: null,
                            candidateType: 0,
                          });

                        if (result.ok) {
                          yield invalidateQueries(queryClient, [
                            "candidateAPI.getExperienceForCandidate",
                          ]);

                          experienceSegmentStore.deletedExperiences.delete(
                            cveExp.id
                          );
                        }
                      });

                      restore();

                      return false;
                    }}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      />
    );

    const itemsComponent = (
      <Observer
        render={() => (
          <>
            {items.map((cveExp) => (
              <div
                className={concatClassnames(["cve-listing"])}
                key={cveExp.id}
              >
                <div className="cve-listing-left">
                  <div className="cve-experience-listing-dates cve-left-dates">
                    <span className="cve-experience-listing-startdate cve-listing-text">
                      {!!cveExp.from &&
                        format(cveExp.from, "MM.yyyy", defDFnsOpt)}
                    </span>
                    <span> - </span>
                    <div className="cve-experience-listing-enddate cve-listing-text">
                      {!!cveExp.to && format(cveExp.to, "MM.yyyy", defDFnsOpt)}
                      {!cveExp.to && L.id("a54c14670d99").loc("Jobber her nå")}
                    </div>
                  </div>
                </div>
                <div className="cve-listing-right">
                  <div className="cve-listing-buttons">
                    <Buttons
                      cveExp={cveExp}
                      experienceSegmentStore={experienceSegmentStore}
                    />
                  </div>
                  <div
                    className="cve-experience-listing-position cve-listing-text"
                    title={L.id("60b4cc0010e5").loc("Stilling")}
                  >
                    {cveExp.position}
                  </div>
                  <div
                    className="cve-experience-listing-employer cve-listing-text"
                    title={L.id("f989c5723933").loc("Arbeidsgiver")}
                  >
                    {cveExp.employer}
                  </div>
                  <div
                    className="cve-experience-listing-description cve-listing-text"
                    title={L.id("5febcd492cb7").loc("Beskrivelse")}
                  >
                    {cveExp.description}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      />
    );

    return (
      <CVEditExperienceListStyles
        className={concatClassnames([
          "cve-list cve-experience-list cve-two-column-list",
          cvEditContext.layoutClassName,
        ])}
      >
        {deletedItemsComponent}
        {itemsComponent}
      </CVEditExperienceListStyles>
    );
  }
);

interface ButtonsProps {
  cveExp: CVEditExperience;
  experienceSegmentStore: CVEditExperienceSegmentStore;
}

const Buttons = ({ cveExp, experienceSegmentStore }: ButtonsProps) => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);
  const L = useLocStore(LocContext.CV);

  const queryClient = useQueryClient();

  const savingUsingToggleButton = observable.box(false);

  return (
    <Observer
      render={() => {
        const isEditingThis =
          experienceSegmentStore.editingExperienceId === cveExp.id;

        return (
          <>
            <Button
              icon="pi pi-trash"
              title={L.id("90acea5a2ddd").loc("Fjern arbeidserfaring")}
              className="p-button-rounded p-button-text p-button-icon-only"
              onClick={flow(function* (evt) {
                evt.preventDefault();

                experienceSegmentStore.deletedExperiences.set(
                  cveExp.id,
                  cveExp
                );

                if (queryStringStore.qs[CVPSearchParams.cvpee] === cveExp.id) {
                  redirect(null, {
                    userInfo,
                    mergeQs: { [CVPSearchParams.cvpee]: null },
                  });
                }

                const result =
                  yield userInfo.fullAPI.candidateAPI.removeExperienceFromCandidate(
                    {
                      showSentryDialogOnError: true,
                      experienceId: Number(cveExp.id),
                      userInfo,
                    }
                  );

                yield invalidateQueries(queryClient, [
                  "candidateAPI.getExperienceForCandidate",
                ]);

                return false;
              })}
            />
            <Button
              icon="pi pi-pencil"
              title={L.id("a3f6ea69fe10").loc("Rediger arbeidserfaring")}
              className={concatClassnames([
                "p-button-rounded p-button-icon-only",
                !isEditingThis && "p-button-text",
              ])}
              loading={savingUsingToggleButton.get()}
              onClick={action((evt) => {
                evt.preventDefault();

                if (isEditingThis) {
                  const expEditorStore =
                    experienceSegmentStore.experienceEditorStore;

                  onCveExperienceEditorCloseButtonClick({
                    experienceSegmentStore,
                    expEditorStore,
                    queryClient,
                    onSaveApiCallStart: () => {
                      savingUsingToggleButton.set(true);
                    },
                    onSaveApiCallEnd: () => {
                      savingUsingToggleButton.set(false);
                    },
                  });
                } else {
                  redirect(null, {
                    userInfo,
                    mergeQs: { [CVPSearchParams.cvpee]: cveExp.id },
                  });
                }

                return false;
              })}
            />
          </>
        );
      }}
    />
  );
};

export const CVEditExperienceListStyles = styled(CVEditListStyles)`
  &.cve-experience-list {
    .cve-experience-listing-position {
      font-weight: bold;
    }
  }
`;
